import { FILE_TYPE } from 'constants/common';

export type FileContentValidationError =
  | 'invalid-content'
  | 'dangerous-extension'
  | 'double-extension'
  | 'mime-mismatch'
  | 'extension-mismatch';

export type FileContentValidationResult = {
  isValid: boolean;
  error?: FileContentValidationError;
  detectedMimeType?: string | null;
};

/** Executable / archive / markup extensions that must never be uploaded. */
const BLACKLISTED_EXTENSIONS = new Set([
  'php',
  'php3',
  'php4',
  'php5',
  'phtml',
  'phar',
  'pl',
  'py',
  'jsp',
  'asp',
  'aspx',
  'htm',
  'html',
  'shtml',
  'xhtml',
  'sh',
  'bash',
  'cgi',
  'exe',
  'bat',
  'cmd',
  'com',
  'js',
  'mjs',
  'cjs',
  'jar',
  'war',
  'zip',
  'rar',
  '7z',
  'gz',
  'tar',
  'dll',
  'so',
  'wasm',
  'svg',
  'xml',
  'svgz'
]);

const MIME_EXTENSION_WHITELIST: Record<string, string[]> = {
  [FILE_TYPE.PNG]: ['png'],
  [FILE_TYPE.JPEG]: ['jpg', 'jpeg'],
  [FILE_TYPE.JPG]: ['jpg', 'jpeg'],
  [FILE_TYPE.WEBP]: ['webp'],
  [FILE_TYPE.PDF]: ['pdf']
};

/** Bytes read for magic-number + HTML sniffing (16 was too small for looksLikeHtml). */
const CONTENT_SNIFF_SIZE = 512;

const normalizeMimeType = (mimeType: string): string => {
  if (mimeType === FILE_TYPE.JPG) return FILE_TYPE.JPEG;
  return mimeType;
};

const getBasename = (filename: string): string => {
  const normalized = filename.replace(/\\/g, '/');
  const segments = normalized.split('/');
  return segments[segments.length - 1] || '';
};

/**
 * Returns only the final extension and optional penultimate extension.
 * Avoids treating every dotted name segment (e.g. my.vacation.photo.jpg) as an extension.
 */
const getFilenameExtensionParts = (
  filename: string
): { lastExtension: string | null; previousExtension: string | null } => {
  const basename = getBasename(filename).toLowerCase();
  const parts = basename.split('.').filter(Boolean);

  if (parts.length < 2) {
    return { lastExtension: null, previousExtension: null };
  }

  return {
    lastExtension: parts[parts.length - 1],
    previousExtension: parts.length >= 3 ? parts[parts.length - 2] : null
  };
};

export const hasBlacklistedExtension = (filename: string): boolean => {
  const { lastExtension, previousExtension } = getFilenameExtensionParts(filename);
  if (lastExtension && BLACKLISTED_EXTENSIONS.has(lastExtension)) return true;
  // Double-extension payloads like shell.php.jpg
  if (previousExtension && BLACKLISTED_EXTENSIONS.has(previousExtension)) return true;
  return false;
};

/** e.g. malware.pdf.png or shell.php.jpg */
export const hasDangerousDoubleExtension = (filename: string): boolean => {
  const { lastExtension, previousExtension } = getFilenameExtensionParts(filename);
  if (!lastExtension || !previousExtension) return false;
  return BLACKLISTED_EXTENSIONS.has(previousExtension) || previousExtension === 'pdf';
};

const matchesSignature = (bytes: Uint8Array, signature: number[], offset = 0): boolean => {
  if (bytes.length < offset + signature.length) return false;
  return signature.every((byte, index) => bytes[offset + index] === byte);
};

const looksLikeHtml = (bytes: Uint8Array): boolean => {
  const sampleLength = Math.min(bytes.length, CONTENT_SNIFF_SIZE);
  let text = '';
  for (let i = 0; i < sampleLength; i++) {
    text += String.fromCharCode(bytes[i]);
  }
  const normalized = text.trimStart().toLowerCase();
  return (
    normalized.startsWith('<!doctype html') ||
    normalized.startsWith('<html') ||
    normalized.startsWith('<head') ||
    normalized.startsWith('<script') ||
    normalized.startsWith('<?xml')
  );
};

/**
 * Detect MIME type from file magic bytes (content), not from the claimed extension/MIME.
 */
export const detectMimeTypeFromContent = async (file: File): Promise<string | null> => {
  const headerSize = Math.min(file.size, CONTENT_SNIFF_SIZE);
  if (headerSize === 0) return null;

  const buffer = await file.slice(0, headerSize).arrayBuffer();
  const bytes = new Uint8Array(buffer);

  if (matchesSignature(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) {
    return FILE_TYPE.PNG;
  }

  if (matchesSignature(bytes, [0xff, 0xd8, 0xff])) {
    return FILE_TYPE.JPEG;
  }

  // RIFF....WEBP
  if (matchesSignature(bytes, [0x52, 0x49, 0x46, 0x46]) && matchesSignature(bytes, [0x57, 0x45, 0x42, 0x50], 8)) {
    return FILE_TYPE.WEBP;
  }

  if (matchesSignature(bytes, [0x25, 0x50, 0x44, 0x46])) {
    return FILE_TYPE.PDF;
  }

  if (looksLikeHtml(bytes)) {
    return 'text/html';
  }

  return null;
};

const isMimeAllowed = (detectedMime: string, allowedMimeTypes: string[]): boolean => {
  const normalizedDetected = normalizeMimeType(detectedMime);
  return allowedMimeTypes.some(allowed => {
    const normalizedAllowed = normalizeMimeType(allowed);
    if (normalizedAllowed.endsWith('/*')) {
      const prefix = normalizedAllowed.slice(0, -1); // e.g. "image/"
      return normalizedDetected.startsWith(prefix);
    }
    return normalizedAllowed === normalizedDetected;
  });
};

const collectAllowedExtensions = (allowedMimeTypes: string[]): Set<string> => {
  const allowedExtensions = new Set<string>();

  allowedMimeTypes.forEach(mime => {
    const normalizedMime = normalizeMimeType(mime);

    if (normalizedMime.endsWith('/*')) {
      const prefix = normalizedMime.slice(0, -1);
      Object.entries(MIME_EXTENSION_WHITELIST).forEach(([key, exts]) => {
        if (normalizeMimeType(key).startsWith(prefix)) {
          exts.forEach(ext => allowedExtensions.add(ext));
        }
      });
      return;
    }

    const mapped = MIME_EXTENSION_WHITELIST[mime] ?? MIME_EXTENSION_WHITELIST[normalizedMime];
    mapped?.forEach(ext => allowedExtensions.add(ext));
  });

  return allowedExtensions;
};

const hasAllowedExtension = (filename: string, allowedMimeTypes: string[]): boolean => {
  const { lastExtension } = getFilenameExtensionParts(filename);
  if (!lastExtension) return false;

  const allowedExtensions = collectAllowedExtensions(allowedMimeTypes);
  if (allowedExtensions.size === 0) return true;
  return allowedExtensions.has(lastExtension);
};

/**
 * Validates upload files beyond browser MIME/extension checks.
 * Rejects content spoofing (e.g. PDF/HTML renamed to .png), double extensions, and blacklisted types.
 */
export const validateUploadFileContent = async (
  file: File,
  allowedMimeTypes?: string[]
): Promise<FileContentValidationResult> => {
  const filename = file.name || '';

  if (hasBlacklistedExtension(filename)) {
    return { isValid: false, error: 'dangerous-extension' };
  }

  if (hasDangerousDoubleExtension(filename)) {
    return { isValid: false, error: 'double-extension' };
  }

  if (allowedMimeTypes?.length && !hasAllowedExtension(filename, allowedMimeTypes)) {
    return { isValid: false, error: 'extension-mismatch' };
  }

  const detectedMimeType = await detectMimeTypeFromContent(file);

  if (!detectedMimeType) {
    return { isValid: false, error: 'invalid-content', detectedMimeType: null };
  }

  if (detectedMimeType === 'text/html') {
    return { isValid: false, error: 'invalid-content', detectedMimeType };
  }

  if (allowedMimeTypes?.length && !isMimeAllowed(detectedMimeType, allowedMimeTypes)) {
    return { isValid: false, error: 'mime-mismatch', detectedMimeType };
  }

  return { isValid: true, detectedMimeType };
};

export const filterFilesByContentValidation = async (
  files: File[],
  allowedMimeTypes?: string[]
): Promise<{ validFiles: File[]; hasInvalidFiles: boolean }> => {
  const results = await Promise.all(files.map(file => validateUploadFileContent(file, allowedMimeTypes)));

  const validFiles = files.filter((_, index) => results[index].isValid);
  const hasInvalidFiles = validFiles.length !== files.length;

  return { validFiles, hasInvalidFiles };
};
